import secureApi from '../../utils/secureApi';
import imageCompression from 'browser-image-compression';

interface Tournament {
  _id: string;
  name: string;
  sports: string;
}

interface ApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: Tournament[];
}

// =========================
// Website/WeeklyReport Tournament APIs
// =========================

// (These functions use /website/ endpoints and are used for dropdowns, Weekly Reports, etc.)
export const getTournamentDropdown = async (search: string = ''): Promise<Tournament[]> => {
  try {
    const params: any = {
      page: 1,
      pageSize: 50,
    };
    if (search) params.search = search;

    const response = await secureApi.get<any>('/api/v1/tournaments/website/list-all-tournaments', {
      params,
    });
    console.log('response:', response);
    
    // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
    if (response && response.data) {
      // If data is an array, return it
      if (Array.isArray(response.data)) {
        return response.data;
      }
      // If data has paginatedResults, return that
      if (response.data.paginatedResults) {
        return response.data.paginatedResults;
      }
    } else if (Array.isArray(response)) {
      return response;
    } else if (response && response.paginatedResults) {
      return response.paginatedResults;
    }
    return [];
  } catch (error) {
    console.error('Failed to fetch tournaments for dropdown:', error);
    return [];
  }
};

// Admin Tournament API
export interface TournamentPayload {
  name: string;
  shortName: string;
  logo: string;
  heroImage?: string;
  location: string;
  startDate: string;
  endDate: string;
  status: string;
  sports: string;
  sortNo: number;
  _id?: string;
}

// =========================
// Admin Tournament APIs (CRUD for Admin Panel)
// =========================

// (These functions use /admin/ endpoints and are used for admin CRUD operations only)
export async function listAdminTournaments(page = 1, pageSize = 10, sport?: string) {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (sport) params.set('sport', sport);
  const response = await secureApi.get<any>(
    `/api/v2/tournaments/admin/get-all-tournaments?${params.toString()}`
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

export async function addTournament(payload: TournamentPayload) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/admin/add?sport=${encodeURIComponent(payload.sports)}`,
    payload
  );
}

export async function editTournament(payload: TournamentPayload) {
  try {
    if (!payload._id) {
      throw new Error('Tournament ID is required to update a tournament.');
    }
    return await secureApi.post<any>(
      `/api/v2/tournaments/manage/update-tournament-details/${payload._id}`,
      payload
    );
  } catch (error: any) {
    console.error('Error editing tournament:', error);
    
    // Enhanced error handling for better user feedback
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Handle validation errors from API
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const validationError = errorData.errors.map((err: any) => err.message || err.msg).join(', ');
        throw new Error(validationError);
      }
      
      // Handle specific error messages
      if (errorData.message) {
        throw new Error(errorData.message);
      }
      
      if (errorData.error) {
        throw new Error(errorData.error);
      }
    }
    
    // Handle network errors
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    
    // Handle HTTP status codes
    switch (error.response?.status) {
      case 400:
        throw new Error('Invalid data provided. Please check your input and try again.');
      case 401:
        throw new Error('Unauthorized. Please log in again.');
      case 403:
        throw new Error('You do not have permission to edit this tournament.');
      case 404:
        throw new Error('Tournament not found.');
      case 409:
        throw new Error('A tournament with this name already exists.');
      case 500:
        throw new Error('Server error. Please try again later.');
      default:
        throw new Error(error.message || 'Failed to update tournament. Please try again.');
    }
  }
}

export async function deleteTournament(_id: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/admin/delete?sport=${encodeURIComponent(sport)}`,
    { _id }
  );
}

export async function getTournamentById(_id: string) {
  try {
    const response = await secureApi.get<any>(
      `/api/v2/tournaments/manage/find-by-id/${_id}`
    );
    console.log('getTournamentById response:', response);
    
    // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
    // secureApi returns response.data from axios, which is the full API response
    if (response && response.data && (response.data._id || response.data.name)) {
      return response.data;
    } else if (response && (response._id || response.name)) {
      // Response is already the tournament data
      return response;
    } else {
      // Fallback: return response as-is
      return response;
    }
  } catch (error) {
    console.error('Error in getTournamentById:', error);
    console.error('Tournament ID:', _id);
    throw error;
  }
}

export async function updateTournamentStatus(_id: string, status: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/admin/update-status?sport=${encodeURIComponent(sport)}`,
    { _id, status }
  );
}

// =========================
// Generic tournament list for scope IDs (Access Management)
// =========================

export interface TournamentListItem {
  _id: string;
  name: string;
  shortName?: string;
  slug?: string;
  status?: string;
}

export async function listTournamentsForScope(): Promise<TournamentListItem[]> {
  try {
    const response = await secureApi.get<any>('/api/v2/tournaments/multi/list');

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch tournaments for scope IDs:', error);
    return [];
  }
}

// =========================
// Tournament Logo Upload
// =========================

export async function uploadTournamentLogo(file: File): Promise<string> {
  try {
    // Compress the image
    const compressedFile = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    });

    // Generate file name
    const ext = compressedFile.type.split('/')[1] || 'jpg';
    const fileName = `tournament-logo-${Date.now()}.${ext}`;
    const context = 'tournament';

    // Get presigned URL
    const presignRes = await secureApi.post<any>(
      '/api/v1/general/presigned-url',
      {
        fileName,
        contentType: compressedFile.type,
        context,
      }
    );

    const presignedUrl = presignRes?.presignedUrl;
    const s3Key = presignRes?.key;

    if (!presignedUrl || !s3Key) {
      throw new Error('Failed to get presigned URL');
    }

    // Upload to S3
    const uploadRes = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': compressedFile.type },
      body: compressedFile,
    });

    if (!uploadRes.ok) {
      throw new Error('Failed to upload image to S3');
    }

    return s3Key;
  } catch (error) {
    console.error('Error uploading tournament logo:', error);
    throw error;
  }
}
